import {
  Component,
  ElementRef,
  Inject,
  ViewChild,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn 
} from '@angular/forms';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogContent,
  MatDialogActions,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import {
  MatNativeDateModule,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DatePipe, CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { TemplateDialogComponent } from '@app/shared/template-dialog/template-dialog.component';
import { environment } from 'src/environments/environment';
import { STATES } from '@app/shared/constants/states';

interface Carriers {
  id: number;
  name: string;
  state: string;
}

interface LocationOption {
  id?: number;
  city: string;
  state: string;
  zip?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  country?: string | null;
  timezone?: string | null;
}

@Component({
  selector: 'app-load-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,

    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatAutocompleteTrigger,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatIconModule,
  ],
  templateUrl: './load-dialog.component.html',
  styleUrl: './load-dialog.component.scss',
  providers: [provideNativeDateAdapter(), DatePipe],
})
export class LoadDialogComponent {

  testing: boolean = false;

  load: any | null = null;
  mode: 'add' | 'edit' = 'add';
  copy = false;

  states = STATES;

  operableOptions = [
    { value: true, name: 'Yes' },
    { value: false, name: 'No' },
  ];

  divisibleOptions = [
    { value: true, name: 'Divisible' },
    { value: false, name: 'Non Divisible' },
  ];

  form!: FormGroup;
  templateForm!: FormGroup;

  originOptions: LocationOption[] = [];
  destinationOptions: LocationOption[] = [];

  filteredOriginOptions: LocationOption[] = [];
  filteredDestinationOptions: LocationOption[] = [];

  loadingOrigin = signal(false);
  loadingDestination = signal(false);

  minSearchLength = 5;
  distance = signal<number | null>(null);

  @ViewChild('originInput') originInput!: ElementRef<HTMLInputElement>;
  @ViewChild('originInput', { read: MatAutocompleteTrigger })
  originTrigger!: MatAutocompleteTrigger;

  @ViewChild('destinationInput')
  destinationInput!: ElementRef<HTMLInputElement>;
  @ViewChild('destinationInput', { read: MatAutocompleteTrigger })
  destinationTrigger!: MatAutocompleteTrigger;

  carriers: Carriers[] = [];
  templates: any[] = [];

  today = this.startOfDay(new Date());

  // Fields needs to be converted
  private dimensionFields = ['length', 'width', 'height', 'well_length'];

  constructor(
    public dialogRef: MatDialogRef<LoadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private authService: AuthService,
    private appService: AppService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private datePipe: DatePipe
  ) {
    this.load = data?.load ?? null;
    this.mode = data?.mode ?? 'add';
    this.copy = data?.copy ?? false;

    const tomorrow = this.addDays(this.today, 1);

    this.form = this.fb.group({
      origin: [null, Validators.required],

      origin_city: [null, Validators.required],
      origin_state: [null, Validators.required],
      origin_zip: [null, Validators.required],
      origin_latitude: [null, Validators.required],
      origin_longitude: [null, Validators.required],
      origin_country: [null, Validators.required],
      origin_timezone: [null, Validators.required],

      origin_start_date: new FormControl<Date | null>(
        this.today,
        [Validators.required, this.notPastDateValidator()]
      ),
      origin_end_date: new FormControl<Date | null>(
        this.today,
        [Validators.required, this.notPastDateValidator()]
      ),

      midpoint: [null],
      roundtrip: [false],

      destination: [null, Validators.required],
      destination_city: [null, Validators.required],
      destination_state: [null, Validators.required],
      destination_zip: [null, Validators.required],
      destination_latitude: [null, Validators.required],
      destination_longitude: [null, Validators.required],
      destination_country: [null, Validators.required],
      destination_timezone: [null, Validators.required],

      destination_start_date: new FormControl<Date | null>(
        null,
        [this.notPastDateValidator()]
      ),
      destination_end_date: new FormControl<Date | null>(
        null,
        [this.notPastDateValidator()]
      ),

      carrier_id: [null, Validators.required],
      carrier_name: [null, Validators.required],
      carrier_base_state: [null, Validators.required],

      weight: [null, Validators.required],
      length: [null, Validators.required],
      well_length: [null, Validators.required],
      width: [null, Validators.required],
      height: [null, Validators.required],

      operable: [null, Validators.required],
      divisible: [null, Validators.required],
      commodity: [null, Validators.required],

      distance: null,
      duration: null,
      rate: null,
      rpm: null,
      cod: null,
      permits_reimbursed: null,
      note: null,
      reference: null,

      tarp: false,
      hazmat: false,
      military_base_access: false,
      twic: false,
      emodal: false,
      outriggers: false,
      self_load: false,
      self_unload: false,

      visible: true,
    });

    this.templateForm = this.fb.group({
      id: [null as number | null],
    });
  }

  ngOnInit(): void {
    if (!this.authService.userCanPostLoads()) {
      this.dialogRef.close();
      return;
    }

    this.getCarriersOptions();
    this.getTemplates();

    if (this.load) {
      this.patchForm(this.load);
    }
  }

  private notPastDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) return null;

      const selected = this.startOfDay(new Date(value));

      return selected < this.today ? { pastDate: true } : null;
    };
  }

  filter(controller: 'origin' | 'destination'): void {
    const inputRef =
      controller === 'origin' ? this.originInput : this.destinationInput;

    const allOptions =
      controller === 'origin' ? this.originOptions : this.destinationOptions;

    const setFiltered =
      controller === 'origin'
        ? (opts: LocationOption[]) => (this.filteredOriginOptions = opts)
        : (opts: LocationOption[]) => (this.filteredDestinationOptions = opts);

    const value = inputRef.nativeElement.value.toLowerCase();

    if (value.length < this.minSearchLength) {
      setFiltered([]);
      return;
    }

    const alreadyInOptions = allOptions.some(
      (opt) => this.formatLocation(opt).toLowerCase() === value
    );

    if (!alreadyInOptions) {
      this.updateOptions(value, controller);
    }

    const matches = allOptions.filter((opt) =>
      this.formatLocation(opt).toLowerCase().includes(value)
    );

    setFiltered(matches);
  }

  updateOptions(search: string, controller: 'origin' | 'destination'): void {
    const setLoading =
      controller === 'origin' ? this.loadingOrigin : this.loadingDestination;

    const setOptions =
      controller === 'origin'
        ? (opts: LocationOption[]) => {
            this.originOptions = opts;
            this.filteredOriginOptions = opts;
          }
        : (opts: LocationOption[]) => {
            this.destinationOptions = opts;
            this.filteredDestinationOptions = opts;
          };

    const trigger =
      controller === 'origin' ? this.originTrigger : this.destinationTrigger;

    setLoading.set(true);

    this.appService.getLocations(search).subscribe({
      next: (res) => {
        const data = Array.isArray(res.data) ? res.data : [];

        setOptions(data);
        setLoading.set(false);

        setTimeout(() => trigger.openPanel());
      },
      error: (err) => {
        console.error('Error:', err);
        setOptions([]);
        setLoading.set(false);
      },
    });
  }

  formatLocation(item: LocationOption | null): string {
    if (!item) return '';

    return `${item.city}, ${item.state}${item?.zip ? ` ${item.zip}` : ''}`;
  }

  onLocationSelected(
    item: LocationOption,
    controller: 'origin' | 'destination'
  ): void {
    if (!item) return;

    if (controller === 'origin') {
      this.form.patchValue({
        origin: this.formatLocation(item),
        origin_city: item.city ?? null,
        origin_state: item.state ?? null,
        origin_zip: item.zip ?? null,
        origin_latitude: item.latitude ?? null,
        origin_longitude: item.longitude ?? null,
        origin_country: item.country ?? 'USA',
        origin_timezone: item.timezone ?? null,
      });
    }

    if (controller === 'destination') {
      this.form.patchValue({
        destination: this.formatLocation(item),
        destination_city: item.city ?? null,
        destination_state: item.state ?? null,
        destination_zip: item.zip ?? null,
        destination_latitude: item.latitude ?? null,
        destination_longitude: item.longitude ?? null,
        destination_country: item.country ?? 'USA',
        destination_timezone: item.timezone ?? null,
      });
    }

    this.getDistance();
  }

  onSubmit(): void {
    if (this.form.invalid) {

      if (this.testing) {
        this.logInvalidFields(this.form);
      }

      this.appService.showSnackBar(
        'Please fill in all required fields.',
        'error'
      );

      this.form.markAllAsTouched();

      return;
    }

    if (this.mode === 'add') {
      this.createLoad();
    }

    if (this.mode === 'edit') {
      this.updateLoad();
    }
  }

  createLoad(): void {

    // Format dates and dimensions before sending to the server
    const formattedData = this.convertDimensionsToInches(
      this.formatFormDates(this.form)
    );

    this.appService.post('loads', formattedData).subscribe({
      next: (success) => {
        this.appService.showSnackBar(success.message, 'success');
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Error:', error);
        this.appService.handleError(error);
        this.appService.showSnackBar('Error creating load.', 'error');
      },
    });
  }

  updateLoad(): void {
    if (!this.load) return;

    console.log('Updating load with ID:', this.load.id);
    console.log('Form is valid:', this.form.valid);

    if (!this.form.valid) {
      this.appService.showSnackBar('Please fill in all required fields.', 'error');
      return;
    }

    const formattedData = this.convertDimensionsToInches(
      this.formatFormDates(this.form)
    );

    this.appService.patch(`loads/${this.load.id}`, formattedData).subscribe({
      next: (success) => {
        this.appService.showSnackBar(success.message, 'success');
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Error:', error);
        this.appService.handleError(error);
        this.appService.showSnackBar('Error updating load.', 'error');
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  deleteLoad(): void {
    if (!this.load) return;

    this.appService
      .confirm('Are you sure you want to delete this load?')
      .subscribe((result) => {
        if (!result) return;

        this.appService.delete(`loads/${this.load.id}`).subscribe({
          next: (response) => {
            this.appService.showSnackBar(response.message, 'success');
            this.dialogRef.close(true);
          },
          error: (err) => {
            this.appService.handleError(err);
            console.error('Error deleting load:', err);
          },
        });
      });
  }

  private formatFormDates(form: FormGroup): any {
    const raw = form.getRawValue();

    return {
      ...raw,
      origin_start_date: this.datePipe.transform(
        raw.origin_start_date,
        'yyyy-MM-dd'
      ),
      origin_end_date: this.datePipe.transform(
        raw.origin_end_date,
        'yyyy-MM-dd'
      ),
      destination_start_date: this.datePipe.transform(
        raw.destination_start_date,
        'yyyy-MM-dd'
      ),
      destination_end_date: this.datePipe.transform(
        raw.destination_end_date,
        'yyyy-MM-dd'
      ),
    };
  }

  getCarriersOptions(): void {
    const user = this.authService.getUserFromStorage();

    if (!user || !Array.isArray(user.carriers)) {
      console.error('User not found or no carriers');
      return;
    }

    this.carriers = user.carriers
      .filter(
        (conn: any) =>
          conn.allowed_to_operate === 'Y' &&
          conn.broker_authority_status === 'A'
      )
      .map((conn: any) => ({
        id: conn.id,
        name: conn.dba_name?.trim() || conn.legal_name,
        state: conn.phy_state?.trim() || '',
      }));

    if (this.carriers.length === 1 && this.mode === 'add') {
      this.form.patchValue({
        carrier_id: this.carriers[0].id,
        carrier_name: this.carriers[0].name,
        carrier_base_state: this.carriers[0].state,
      });
    }
  }

  onCarrierChange(event: any): void {
    const selectedCarrier = this.carriers.find((c) => c.id === event.value);

    if (selectedCarrier) {
      this.form.patchValue({
        carrier_id: selectedCarrier.id,
        carrier_name: selectedCarrier.name,
        carrier_base_state: selectedCarrier.state,
      });
    } else {
      this.form.patchValue({
        carrier_id: null,
        carrier_name: null,
        carrier_base_state: null,
      });
    }
  }

  getDistance(): void {
    const origin = this.form.get('origin')?.value;
    const destination = this.form.get('destination')?.value;

    if (!origin || !destination) return;

    this.appService.getDistance(origin, destination).subscribe({
      next: (res) => {
        this.distance.set(res.data.driving.distance);
        this.form.get('distance')?.setValue(this.distance());
        this.form.get('duration')?.setValue(res.data.driving.duration);
      },
      error: (err) => {
        console.error('Error:', err);
        this.distance.set(null);
        this.form.get('distance')?.setValue(null);
        this.form.get('duration')?.setValue(null);
        this.appService.handleError(err);
      },
    });
  }

  patchForm(load: any): void {
    let origin_start_date = this.parseDateLocal(load.origin_start_date);
    let origin_end_date = this.parseDateLocal(load.origin_end_date);
    let destination_start_date = this.parseDateLocal(load.destination_start_date);
    let destination_end_date = this.parseDateLocal(load.destination_end_date);

    if (this.copy) {
      let travelDays = 1;

      if (load.distance && !isNaN(load.distance)) {
        const milesPerDay = 500;
        travelDays = Math.ceil(load.distance / milesPerDay);
      }

      origin_start_date = this.today;
      origin_end_date = this.today;

      destination_start_date = this.addDays(this.today, travelDays);
      destination_end_date = this.addDays(this.today, travelDays);

      this.mode = 'add';
    }

    this.form.patchValue({
      ...this.convertDimensionsToFeet(load),
      origin_start_date,
      origin_end_date,
      destination_start_date,
      destination_end_date,
    });

    this.distance.set(load.distance || null);

    if (this.mode === 'edit') {
      if (!environment.allowLoadOriginLocationChange) {
        this.form.get('origin')?.disable();
      }

      if (!environment.allowLoadDestinationLocationChange) {
        this.form.get('destination')?.disable();
      }
    }
  }

  toFeetAndInches(inches: number): string {
    if (!inches || inches <= 0) return '0 in';

    const feet = Math.floor(inches / 12);
    const remainder = inches % 12;

    return `${feet} ft ${remainder} in`;
  }

  getTemplates(): void {
    this.appService.getMyLoadTemplates().subscribe({
      next: (res) => {
        this.templates = res.data;
      },
      error: (err) => {
        this.appService.handleError(err);
      },
    });
  }

  createTemplate(mode: 'copy' | 'create' = 'create'): void {
    if (mode === 'copy' && !this.templateForm.get('id')?.value) {
      console.error('No template selected for copying');
      return;
    }

    const selectedTemplate = this.templates.find(
      (t: any) => t.id === this.templateForm.get('id')?.value
    );

    const formValue = this.getCleanFormValue(this.form);

    const dialogRef = this.dialog.open(TemplateDialogComponent, {
      data: {
        mode,
        name: selectedTemplate?.name ?? null,
        type: 'load',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.name) return;

      const payload = {
        name: result.name,
        payload: formValue,
      };

      this.appService.post('loads/templates', payload).subscribe({
        next: (response) => {
          this.appService.showSnackBar(response.message, 'success');

          const templateId = response.data.id;

          this.getTemplates();
          this.templateForm.get('id')?.setValue(templateId);
        },
        error: (err) => this.appService.handleError(err),
      });
    });
  }

  updateTemplate(): void {
    const formValue = this.getCleanFormValue(this.form);

    const selectedTemplate = this.templates.find(
      (t: any) => t.id === this.templateForm.get('id')?.value
    );

    if (!selectedTemplate) {
      console.error('No template selected for update');
      return;
    }

    const dialogRef = this.dialog.open(TemplateDialogComponent, {
      data: {
        mode: 'update',
        name: selectedTemplate?.name ?? null,
        type: 'load',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.name) return;

      const payload = {
        name: result.name,
        payload: formValue,
      };

      this.appService.patch(`loads/templates/${selectedTemplate.id}`, payload).subscribe({
        next: (response) => {
          this.appService.showSnackBar(response.message, 'success');
          this.getTemplates();
        },
        error: (err) => this.appService.handleError(err),
      });
    });
  }

  deleteTemplate(): void {
    if (!this.templateForm.get('id')?.value) {
      console.error('No template selected for deletion');
      return;
    }

    this.appService
      .confirm('Are you sure you want to delete this template?')
      .subscribe((ok) => {
        if (!ok) return;

        const id = this.templateForm.value.id;
        if (!id) return;

        this.appService.delete(`loads/templates/${id}`).subscribe({
          next: (response) => {
            this.appService.showSnackBar(response.message, 'success');
            this.clearForm();
            this.getTemplates();
            this.templateForm.reset();
            this.onTemplateChange();
          },
          error: (err) => this.appService.handleError(err),
        });
      });
  }

  onTemplateChange(): void {
    const id = this.templateForm.value.id as number | null;
    const selected = this.templates.find((t: any) => t.id === id);

    if (!selected) {
      this.clearForm();
      return;
    }

    this.patchTemplate(selected.payload ?? {});
  }

  onRateChange(): void {
    const rate = this.form.get('rate')?.value;
    const distance = this.form.get('distance')?.value;

    if (rate != null && distance != null && distance > 0) {
      const rpm = Number((rate / distance).toFixed(2));
      this.form.get('rpm')?.setValue(rpm);
    } else {
      this.form.get('rpm')?.setValue(null);
    }
  }

  private patchTemplate(payload: any): void {
    const p = payload ?? {};
    const out: any = { ...p };

    const isNum = (v: any) => typeof v === 'number' && Number.isFinite(v);

    if (isNum(p.pickup_window_days)) {
      out.origin_start_date = this.today;
      out.origin_end_date = this.addDays(this.today, p.pickup_window_days);
    }

    if (isNum(p.start_to_start_days)) {
      const base = out.origin_start_date ?? this.today;
      out.destination_start_date = this.addDays(base, p.start_to_start_days);
    }

    if (isNum(p.delivery_window_days) && out.destination_start_date) {
      out.destination_end_date = this.addDays(
        out.destination_start_date,
        p.delivery_window_days
      );
    }

    delete out.pickup_window_days;
    delete out.start_to_start_days;
    delete out.delivery_window_days;

    this.clearForm();
    this.form.patchValue(out);
  }

  private clearForm(preserveCarrier = true): void {
    const snapshot = this.form.getRawValue();

    this.form.reset();

    if (preserveCarrier) {
      this.form.patchValue(
        {
          carrier_id: snapshot.carrier_id ?? null,
          carrier_name: snapshot.carrier_name ?? null,
          carrier_base_state: snapshot.carrier_base_state ?? null,
        },
        { emitEvent: false }
      );
    }

    this.distance.set(null);
  }

  private getCleanFormValue(form: FormGroup): any {
    const result: any = {};
    const raw: any = {};

    Object.keys(form.controls).forEach((key) => {
      const value = form.get(key)?.value;

      if (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        raw[key] =
          value instanceof Date
            ? this.datePipe.transform(value, 'yyyy-MM-dd')
            : value;
      }
    });

    result.pickup_window_days = this.daysBetween(
      raw.origin_start_date,
      raw.origin_end_date
    );

    result.start_to_start_days = this.daysBetween(
      raw.origin_start_date,
      raw.destination_start_date
    );

    result.delivery_window_days = this.daysBetween(
      raw.destination_start_date,
      raw.destination_end_date
    );

    const exclude = new Set([
      'origin_start_date',
      'origin_end_date',
      'destination_start_date',
      'destination_end_date',
      'carrier_id',
      'carrier_name',
      'carrier_base_state',
    ]);

    Object.keys(raw).forEach((key) => {
      if (!exclude.has(key)) {
        result[key] = raw[key];
      }
    });

    Object.keys(result).forEach((key) => {
      if (result[key] === undefined) {
        delete result[key];
      }
    });

    return result;
  }

  private daysBetween(a?: string | Date, b?: string | Date): number | undefined {
    const toDate = (x?: string | Date) =>
      x instanceof Date
        ? new Date(x.getFullYear(), x.getMonth(), x.getDate())
        : typeof x === 'string'
        ? new Date(x + 'T00:00:00')
        : undefined;

    const d1 = toDate(a);
    const d2 = toDate(b);

    return d1 && d2
      ? Math.round((d2.getTime() - d1.getTime()) / 86400000)
      : undefined;
  }

  private parseDateLocal(dateString?: string | null): Date | null {
    if (!dateString) return null;

    const parts = dateString.split('-').map(Number);

    if (parts.length !== 3 || parts.some(isNaN)) {
      return null;
    }

    const [year, month, day] = parts;

    return new Date(year, month - 1, day);
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private addDays(base: Date, days: number): Date {
    const d = this.startOfDay(base);
    d.setDate(d.getDate() + days);
    return d;
  }

  private logInvalidFields(form: FormGroup): void {
    const invalidFields: Record<string, any> = {};

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);

      if (control?.invalid) {
        invalidFields[key] = {
          value: control.value,
          errors: control.errors,
        };
      }
    });

    console.table(invalidFields);
  }

  openGoogleMaps(): void {
    const origin = this.form.get('origin')?.value;
    const destination = this.form.get('destination')?.value;

    if (!origin || !destination) {
      this.appService.showSnackBar(
        'Please fill in both origin and destination to open Google Maps.',
        'error'
      );
      return;
    }

    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
      origin
    )}&destination=${encodeURIComponent(destination)}`;

    window.open(url, '_blank');
  }

  toFeetAndInchesFromFeet(value: number | string | null): string {
    const feetValue = Number(value);

    if (!feetValue || feetValue <= 0) {
      return '';
    }

    const totalInches = Math.round(feetValue * 12);
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet} ft ${inches} in`;
  }

  private convertDimensionsToInches(payload: any): any {
    this.dimensionFields.forEach((field) => {
      payload[field] = payload[field]
        ? Math.round(Number(payload[field]) * 12)
        : null;
    });

    return payload;
  }

  private convertDimensionsToFeet(payload: any): any {
    const out = { ...payload };

    this.dimensionFields.forEach((field) => {
      out[field] =
        out[field] !== null && out[field] !== undefined && out[field] !== ''
          ? Number((Number(out[field]) / 12).toFixed(1))
          : null;
    });

    return out;
  }
}